// El cliente HTTP: la instancia de axios, sus interceptores y la URL base.
// Lo unico que comparten todos los demas.
//
// Vivia en `lib/api.js` junto con otros veintidos apartados (R4.5). Quien lo importa
// no se entero: `@/lib/api` sigue exportando exactamente lo mismo.

import axios from "axios";
import { reportSuccess, reportFailure } from "@/lib/systemStatusStore";
import { toast } from "sonner";
import { creaDespertador } from "@/lib/despertador.mjs";
import { anuncia, laSubida } from "@/lib/subidaDeNivel.mjs";

// Exportada porque la usan dos ficheros: `API` aqui al lado y `fileUrl` en
// cms.js. Cuando vivian juntos en api.js no hacia falta; al partirlo, si.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API = `${BACKEND_URL}/api`;

// `withCredentials` NO ES UN DETALLE: sin el, la cookie de sesion no existe.
//
// El backend manda `access_token` como cookie `httponly`, que es la forma segura de guardar
// una sesion: el JavaScript de la pagina no puede leerla, asi que un XSS no se la lleva. Pero
// desde el dia del despliegue la web vive en `academialpha.com` y la API en
// `api.academialpha.com`: distinto ORIGEN. Y en una peticion sin credenciales el navegador no
// manda cookies y ademas TIRA el `Set-Cookie` de la respuesta sin decir nada.
//
// O sea que la cookie se estaba mandando desde el servidor y muriendo en el navegador, y toda
// la autenticacion de produccion colgaba del token en `localStorage` —el que un XSS si puede
// leer—, mientras el comentario de esta misma linea lo llamaba "fallback". Un mecanismo que ya
// no describe nada seguia mandando, y no daba ningun error: daba silencio.
//
// Con esto la cookie vuelve a llegar. Los dos apellidos que lo hacen posible:
//   - `academialpha.com` y `api.academialpha.com` son el MISMO SITIO (mismo dominio
//     registrable), asi que el `samesite=lax` de la cookie no estorba;
//   - CORS ya venia con `allow_credentials=True` y la lista de origenes nombrados uno a uno
//     (`backend/server.py`), que es justo lo que exige el navegador para credenciales.
// Lo comprueba `tools/check_sesion.py`, que muerde si falta cualquiera de los dos.
export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Y CON LA COOKIE LLEGANDO, EL TOKEN SE FUE. Aqui vivia un interceptor que sacaba la llave de
// sesion de `localStorage` y la ponia como cabecera `Authorization`. Ya no hay ninguna llave de
// sesion en el almacenamiento de la pagina: la sesion vive solo en la cookie `httponly`, que es
// la que un XSS no puede leer.
//
// Se quito el 2 de septiembre y DESPUES de comprobarlo en produccion —borrar la llave, recargar
// y seguir dentro—, que es el orden que importa: quitarlo a ciegas el dia anterior habria dejado
// fuera a los 180 alumnos, porque hasta esa mañana era lo unico que funcionaba.
//
// El backend sigue aceptando `Authorization: Bearer` (`app/deps/auth.py`), y eso no sobra: es
// por donde entran las pruebas y por donde entraria un cliente movil.

/**
 * UN 429 TIENE QUE DECIRSE, Y NO LO DECIA NADIE.
 *
 * `systemStatusStore` solo mira los fallos de red y los de 500 para arriba, que es lo correcto
 * -un freno no es una caida y sacar la pantalla de reconexion seria mentir-. Pero el efecto era
 * que **un 429 no lo veia nadie**: las llamadas rebotaban, las pantallas se quedaban a medias, y
 * ni un aviso. El profesor veia la plataforma rota sin una sola pista, que es la averia con
 * nombre de esta casa.
 *
 * Y PASA DE VERDAD. El 19 de septiembre de 2026, despues de un corte de la base, la pantalla de
 * reconexion reintentaba, cada reintento gastaba peticiones, se agoto el limite y todo empezo a
 * dar 429 en silencio.
 *
 * SE DICE LO QUE DICE EL SERVIDOR, que ya manda una frase con los segundos que faltan
 * (`domain/cadencia.py`, `como_se_dice`). Escribir aqui otra frase seria un segundo sitio donde
 * mantener el mismo texto, y el dia que cambie el limite uno de los dos mentiria.
 *
 * UNO CADA VEZ. Una pantalla hace diez llamadas al abrirse: si se frena, se frenan las diez, y
 * diez avisos iguales apilados no informan, tapan. Mientras hay uno puesto, los demas se callan.
 *
 * Y ESTE AVISO NO LO VEIA NADIE, HASTA EL 10 DE OCTUBRE. Llamaba al `toast` de shadcn
 * (`hooks/use-toast`), pero el armazon solo monta el `Toaster` de `sonner` (`App.js`): el aviso
 * se apuntaba en una lista que ningun componente pintaba. Era la misma averia que venia a
 * arreglar —el freno en silencio— metida dentro del arreglo. Ahora va por `sonner`, como el resto
 * de avisos de la plataforma, y lo vigila `scripts/prueba_avisos.mjs`.
 */
let avisando = false;

function avisaDelFreno(error) {
  if (error?.response?.status !== 429 || avisando) return;
  avisando = true;
  const dice = error.response.data?.detail;
  const espera = Number(error.response.headers?.["retry-after"]) || 0;
  toast("Vas demasiado rápido", {
    id: "freno",
    description: dice || "Demasiadas peticiones seguidas. Espera unos segundos y vuelve a probar.",
  });
  // Se vuelve a poder avisar cuando pase la espera que dice el propio servidor, con un suelo de
  // tres segundos por si la cabecera no viene: sin suelo, una rafaga daria un aviso por llamada.
  setTimeout(() => { avisando = false; }, Math.max(3, espera) * 1000);
}

/** Si el servidor tarda porque estaba dormido, se dice. El porqué, en `lib/despertador.mjs`. */
const despertador = creaDespertador({
  avisa: () => toast("El servidor se está despertando", {
    id: "despertando",
    duration: Infinity,
    description: "Si hacía rato que no entraba nadie, tarda hasta un minuto en arrancar. "
      + "No hace falta recargar.",
  }),
  calla: () => toast.dismiss("despertando"),
});

api.interceptors.request.use((config) => {
  despertador.sale();
  return config;
});

// Detecta fallos de red/servidor repetidos para activar el Recovery Mode global (ver context/SystemStatusContext.jsx)
api.interceptors.response.use(
  (response) => {
    despertador.contesta();
    reportSuccess();
    // LA SUBIDA DE NIVEL SE OYE AQUI, Y SOLO AQUI (J12, 21 de octubre de 2026). El servidor
    // lleva meses diciendola —`boss_service.add_xp` devuelve el parte entero y lo reenvian
    // los cofres, el test, el pago del Mundo y las metas— y en la pantalla no pasaba nada
    // salvo al ganarle a un Jefe, que era el unico sitio que la recogia a mano.
    //
    // POR QUE EN EL INTERCEPTOR Y NO EN CADA PANTALLA. Porque eso ya se intento sin querer:
    // la regla «avisa cuando subas» se cumplia **acordandose**, y se cumplio en uno de cuatro
    // sitios. Aqui pasa TODA respuesta de la casa, asi que lo recoge tambien el quinto sitio,
    // el que nadie ha escrito todavia. Es la misma forma que `AlPrincipio`: una regla que se
    // cumple en un sitio en vez de cincuenta pantallas acordandose.
    //
    // Y NO ESTORBA: `laSubida` devuelve `null` salvo que el cuerpo traiga un parte Y diga que
    // hubo subida, y `anuncia` se calla si alguien ha pedido silencio —la pantalla del Jefe,
    // que ya lo celebra con su tarjeta—.
    try {
      anuncia(laSubida(response?.data));
    } catch {
      // Una ceremonia no puede tumbar una respuesta: lo que se pierde es la fiesta.
    }
    return response;
  },
  (error) => {
    // UN ERROR CON RESPUESTA TAMBIÉN ES UN SERVIDOR DESPIERTO: un 401 o un 429 los manda él.
    if (error?.response) despertador.contesta();
    reportFailure(error);
    avisaDelFreno(error);
    return Promise.reject(error);
  },
);
